import {Heading, ProductCard, Text} from '~/modules';
import {LoaderArgs, redirect} from '@shopify/remix-oxygen';

import {BaseLayout} from '~/modules/Layout/components/BaseLayout';
import {Collection} from '~/gql/generated';
import {Container} from '~/modules/Layout/components/Container';
import {ProductsProvider} from '~/modules/Product/context/ProductsContext';
import {SeoHandleFunction} from '@shopify/hydrogen';
import {getLoaderExpertName} from '~/modules/Common/util';
import {getPost} from '~/modules/Product/loaders/postsLoader';
import {useLoaderData} from '@remix-run/react';
import {useProducts} from '~/modules/Product/hooks/useProducts';

const seo: SeoHandleFunction<typeof loader> = ({data: {expertName, post}}) => {
  return {
    title: `${expertName} – ${post.name}`,
    description: post.description,
  };
};

export const handle = {
  seo,
};

export async function loader({
  context: {client, storefront},
  params,
  request,
}: LoaderArgs) {
  const expertName = getLoaderExpertName(params, request);
  if (!expertName) {
    return redirect('/404');
  }

  const {post, shopifyProducts, products} = await getPost(
    params.id!,
    expertName,
    client,
    storefront,
    false,
  );

  return {
    post: post as Collection,
    shopifyProducts,
    products,
    expertName,
  };
}

export default function PostsPage() {
  const {post, shopifyProducts, products} = useLoaderData<typeof loader>();
  return (
    <BaseLayout>
      <Container>
        <div className="flex flex-col md:flex-row md:py-8 md:pl-4">
          <div className="flex flex-col gap-4 md:flex-none md:basis-[400px]">
            <div className="bg-codGray800 md:bg-white ">
              <div className="w-full overflow-hidden">
                <img
                  src={post.mediaUrls?.[0] || ''}
                  alt={post.title}
                  className="block w-full md:max-w-[400px] object-contain relative"
                />
              </div>
            </div>
            <Text size="copy" className="px-2">
              {post.description}
            </Text>
          </div>
          <ProductsProvider
            apiProducts={products || []}
            shopifyProducts={shopifyProducts?.nodes || []}
          >
            <ShopThisGrid />
          </ProductsProvider>
        </div>
      </Container>
    </BaseLayout>
  );
}

const ShopThisGrid = () => {
  const {products} = useProducts();
  return (
    <div className="px-2 md:pl-16 mt-8 md:mt-0">
      <Heading size="headlineSm" as="h1">
        Shop this post
      </Heading>
      <div className="grid grid-cols-2 gap-4 mt-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} theme="storefront" />
        ))}
      </div>
    </div>
  );
};
